import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { callSheetApi } from "@/lib/api";
import { Field } from "./helpers";

export function EmailDialog(props) {
  // Force remount each time it opens so state initializes from latest data.
  return props.open ? <EmailDialogInner {...props} /> : null;
}

function EmailDialogInner({ open, onOpenChange, projectId, dayId, project, team, callSheet, totalDays, onSendStart, onSent }) {
  const autoEmails = useMemo(() => {
    const ids = [
      ...((callSheet?.talent || []).map((t) => t.team_id)),
      ...((callSheet?.crew || []).map((c) => c.team_id)),
      ...((callSheet?.clients || []).map((c) => c.team_id)),
    ];
    const emails = ids
      .map((id) => team.find((m) => m.id === id)?.email)
      .filter(Boolean);
    return [...new Set(emails)];
  }, [callSheet, team]);

  const dayLabel = callSheet?.day_label?.trim() || `Day ${callSheet?.day_number || 1}`;
  const daySuffix = (totalDays && totalDays > 1) ? ` — ${dayLabel}` : "";

  const [recipients, setRecipients] = useState(autoEmails.join(", "));
  const [subject, setSubject] = useState(
    `Call Sheet — ${project?.name || ""}${daySuffix}${project?.job_number ? ` (Job #${project.job_number})` : ""}`
  );
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const list = recipients
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    try {
      await onSendStart();
      const payload = {
        recipients: list,
        subject,
        message: message ? `<p>${message.replace(/\n/g, "<br/>")}</p>` : undefined,
      };
      const res = dayId
        ? await callSheetApi.emailDay(projectId, dayId, payload)
        : await callSheetApi.email(projectId, payload);
      const sentCount = (res?.sent || []).length;
      const errCount = (res?.errors || []).length;
      if (errCount > 0) {
        toast.error(
          `${sentCount} sent, ${errCount} failed: ${res.errors[0]?.error || "unknown error"}`
        );
      } else {
        toast.success(
          `Sent to ${sentCount} recipient${sentCount === 1 ? "" : "s"} · awaiting confirmation`
        );
      }
      onSent && (await onSent());
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Email Call Sheet
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {(totalDays && totalDays > 1) && (
              <span className="block mb-2 text-amber-500/90">
                Sending <strong>{dayLabel}</strong> only. Other days have their own recipients and confirmations.
              </span>
            )}
            PDF attached automatically. Each recipient gets a unique &ldquo;Confirm
            Receipt&rdquo; link &mdash; when they click it, a green check appears
            next to their name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Recipients (comma-separated)">
            <Textarea
              data-testid="email-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="greg@example.com, ashley@example.com"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[60px] font-mono text-sm"
            />
          </Field>
          <Field label="Subject">
            <Input
              data-testid="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </Field>
          <Field label="Message (optional)">
            <Textarea
              data-testid="email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any extra notes for recipients…"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[80px]"
            />
          </Field>
        </div>
        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="send-email-btn"
            onClick={handleSend}
            disabled={sending}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            {sending ? "Sending…" : "Send PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
